import { useEffect, useState } from "react";
import { ArrowRight, History, Scan, MapPin, Gauge } from "lucide-react";
import { PLANS } from "@garageregister/shared";

const FREE_VEHICLES = PLANS.free.limits.maxVehiclesPerGarage;
const FREE_SCANS = PLANS.free.limits.freeScansAllowed;

const APP_STORE_URL = "https://apps.apple.com/us/app/revhead-pro/id6760721252";
const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.garageregister.app&hl=en_US";

/**
 * Platform-aware CTA label. SSR/prerender default is "Download free";
 * after hydration the label flips to the iOS/Android specific copy so
 * the button reads in the user's vocabulary. All three labels target
 * the same `/get` route — the redirect Cloud Function handles routing.
 */
function useDownloadCtaLabel(): string {
  const [label, setLabel] = useState("Download free");
  useEffect(() => {
    if (typeof navigator === "undefined") return;
    const ua = navigator.userAgent.toLowerCase();
    if (/iphone|ipad|ipod/.test(ua)) setLabel("Download for iPhone");
    else if (/android/.test(ua)) setLabel("Download for Android");
    else setLabel("Get the app");
  }, []);
  return label;
}

export function Hero() {
  const ctaLabel = useDownloadCtaLabel();

  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-[#0C1222] via-[#0C1222] to-[#151D2E]" />
      <div className="absolute left-1/2 top-0 -translate-x-1/2 h-[600px] w-[800px] rounded-full bg-[#4DB8C7]/5 blur-3xl" />

      <div className="relative mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-28 lg:px-8 lg:py-36">
        <div className="text-center">
          <div className="mx-auto mb-6 inline-flex items-center gap-2 rounded-full border border-[#253448] bg-[#151D2E] px-4 py-1.5 text-sm text-[#7A8FA3]">
            <span className="h-2 w-2 rounded-full bg-[#4ADE80] animate-pulse" />
            Free forever. No credit card required.
          </div>

          <h1 className="font-heading font-bold tracking-tight text-[#E8EDF2] text-[clamp(28px,8vw,64px)] leading-[1.05]">
            Your garage.{" "}
            <span className="text-[#4DB8C7]">Your records.</span>
            <br />
            Your story.
          </h1>

          <p className="mx-auto mt-6 max-w-2xl text-[#7A8FA3] text-[clamp(16px,4vw,22px)] leading-snug">
            Free to use forever — log up to {FREE_VEHICLES} vehicles, unlimited
            service + mods, and {FREE_SCANS} free AI document scans. Pro unlocks
            unlimited everything. First month of Pro is free on annual.
          </p>

          <div className="mt-8 flex flex-col items-center gap-4 sm:mt-10 sm:flex-row sm:justify-center">
            <a
              href="/get"
              className="group inline-flex w-full max-w-xs items-center justify-center gap-2 rounded-xl bg-[#4DB8C7] px-8 py-3.5 text-base font-semibold text-[#0C1222] shadow-[0_0_20px_rgba(77,184,199,0.25)] transition-all hover:bg-[#3DA8B7] hover:shadow-[0_0_30px_rgba(77,184,199,0.35)] sm:w-auto"
            >
              {ctaLabel}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </a>
            <a
              href="#pricing"
              className="inline-flex w-full max-w-xs items-center justify-center gap-2 rounded-xl border border-[#253448] px-8 py-3.5 text-base font-medium text-[#E8EDF2] transition-colors hover:border-[#4DB8C7]/50 hover:bg-[#151D2E] sm:w-auto"
            >
              See Pro features
            </a>
          </div>

          {/* Above-the-fold store badges on mobile only. Desktop scrolls down
              to MobileAppSection for the same content with more breathing room. */}
          <div className="mt-6 flex items-center justify-center gap-3 md:hidden">
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Download on the App Store"
              className="h-[44px] w-[140px] overflow-hidden rounded-[8px] transition-opacity hover:opacity-80"
            >
              <img src="/badges/app-store.svg" alt="" className="h-full w-full object-cover" />
            </a>
            <a
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Get it on Google Play"
              className="h-[44px] w-[140px] overflow-hidden rounded-[8px] border border-[#a6a6a6] transition-opacity hover:opacity-80"
            >
              <img src="/badges/google-play.png" alt="" className="h-full w-full object-cover" />
            </a>
          </div>

          <div className="mx-auto mt-12 grid max-w-2xl grid-cols-2 gap-6 sm:mt-16 sm:grid-cols-4 sm:gap-8">
            <div className="text-center">
              <div className="flex justify-center mb-2">
                <History className="h-5 w-5 text-[#4DB8C7]" />
              </div>
              <p className="text-sm font-semibold text-[#E8EDF2]">Past + present rides</p>
              <p className="text-xs text-[#7A8FA3]">Every car you&apos;ve ever owned</p>
            </div>
            <div className="text-center">
              <div className="flex justify-center mb-2">
                <Scan className="h-5 w-5 text-[#4DB8C7]" />
              </div>
              <p className="text-sm font-semibold text-[#E8EDF2]">AI scans service + mods</p>
              <p className="text-xs text-[#7A8FA3]">Receipts, invoices, dyno sheets</p>
            </div>
            <div className="text-center">
              <div className="flex justify-center mb-2">
                <MapPin className="h-5 w-5 text-[#4DB8C7]" />
              </div>
              <p className="text-sm font-semibold text-[#E8EDF2]">Tag rides at meets</p>
              <p className="text-xs text-[#7A8FA3]">Spotted, RSVP, check-in</p>
            </div>
            <div className="text-center">
              <div className="flex justify-center mb-2">
                <Gauge className="h-5 w-5 text-[#4DB8C7]" />
              </div>
              <p className="text-sm font-semibold text-[#E8EDF2]">Track performance</p>
              <p className="text-xs text-[#7A8FA3]">Dyno, lap, drag, 0-60</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
